import type { ReactNode } from 'react';
import { useTheme } from '@evi/skins';
import { DefaultKpiCard } from '../themes/default/DefaultKpiCard.js';
import { LuceKpiCard } from '../themes/luce/LuceKpiCard.js';
import { fitValueChars } from '../themes/kpiFit.js';
import type { LabelTone } from '../components/LabelPill.js';

export interface KpiCardProps {
  label: string;
  value: ReactNode;
  unit?: string;
  delta?: { value: string; direction: 'up' | 'down' | 'flat' };
  /** Secondary muted line under the value. Rendered in both variants. */
  meta?: string;
  /** Legacy label tone — was only consumed by the removed KpiTile
   *  path. Kept as an optional no-op so admin widgets that still pass
   *  it don't need to be touched; will be dropped in a follow-up once
   *  every call site migrates. */
  tone?: LabelTone;
  delayMs?: number;
  /** For a value that can run long in a narrow tile (a seven-figure
   *  total). The value stays on one line, sized to the tile's width and
   *  never broken mid-number; the label and the meta line wrap at word
   *  boundaries with tighter spacing, so the stack fits a two-row
   *  dashboard cell from 1024 to 1920 px. See ../themes/kpiFit.ts. */
  fit?: boolean;
}

/**
 * Theme-aware KPI tile.
 *   Default → clean SaaS card with delta pill and tinted background.
 *   Luce    → Ferrari instrument-cluster readout — bezel border, mono
 *             numerals, amber unit suffix, ▲/▼/— direction arrow.
 *
 * Both variants share the same KpiCardProps shape so the caller is
 * unaware of which is rendering. `tone` is ignored by the new Luce
 * variant — consumers can leave it set; it's effectively a no-op.
 */
export function KpiCard({ label, value, unit, delta, meta, delayMs, fit }: KpiCardProps) {
  const { theme } = useTheme();
  const fitChars = fit ? fitValueChars(value, unit) : undefined;
  if (theme.id === 'luce') {
    const luceProps: Parameters<typeof LuceKpiCard>[0] = {
      label,
      value,
      ...(unit !== undefined ? { unit } : {}),
      ...(delta ? { delta } : {}),
      ...(meta ? { meta } : {}),
      ...(delayMs !== undefined ? { delayMs } : {}),
      ...(fitChars !== undefined ? { fitChars } : {}),
    };
    return <LuceKpiCard {...luceProps} />;
  }
  // meta is default-only
  const props: Parameters<typeof DefaultKpiCard>[0] = {
    label,
    value: unit ? (
      <>
        {value}
        <span
          style={{
            fontSize: 12,
            color: 'var(--evi-text-mute)',
            marginLeft: 4,
            fontWeight: 400,
            letterSpacing: 1,
          }}
        >
          {unit}
        </span>
      </>
    ) : (
      value
    ),
    ...(delta ? { delta } : {}),
    ...(meta ? { meta } : {}),
    ...(delayMs !== undefined ? { delayMs } : {}),
    ...(fitChars !== undefined ? { fitChars } : {}),
  };
  return <DefaultKpiCard {...props} />;
}
